// แยกยอดของเอกสาร 1 ใบ ออกตามประเภทภาษี
// ใช้ร่วมกันระหว่างตารางรายงานภาษีขาย (report-detail.vue)
// กับยอดสรุปท้ายรายงาน (store/collection/docs.js) เพื่อไม่ให้ตัวเลขสองที่ไม่ตรงกัน
//
// จำนวนเงินทุกตัวเป็นหน่วยสตางค์ เหมือนกับที่เก็บใน Firestore
//
// ประเภทภาษีอ่านจาก `SLTS.ATT.TypeCode` (คีย์ย่อ `TC`) ซึ่งเป็นค่าที่ถูกส่งเข้า
// <ram:TypeCode> ของ XML ที่ยื่นกรมสรรพากรตรง ๆ ตามรหัส UN/EDIFACT 5153:
//   VAT = ภาษีมูลค่าเพิ่ม (มีอัตราเสมอ แม้อัตรานั้นจะเป็น 0)
//   FRE = ยกเว้นภาษี ไม่มีอัตราภาษีติดมาด้วย
// ตัวเซ็นเอกสารแยกสองอย่างนี้อยู่แล้ว (etax-signer/src/modules/xmler.js) — บรรทัด FRE
// ถูกเขียนลง XML โดยไม่มี CalculatedRate เพราะ "ยกเว้น" ไม่ใช่ "เสียภาษีในอัตราศูนย์"

function getLineVatRate(line) {
  const rate = line["SLTS.ATT.CalculatedRate"] ?? line["CR"]
  if (rate === undefined || rate === null || rate === "") {
    return null
  }
  const value = Number(rate)
  return Number.isFinite(value) ? value : null
}

function getLineAmount(line) {
  const value = Number(line["SLTS.STSLMS.NetLineTotalAmount"] ?? line["NA"] ?? 0)
  return Number.isFinite(value) ? value : 0
}

function taxTypeOf(source, fullKey, shortKey) {
  const code = source[fullKey] ?? (shortKey ? source[shortKey] : undefined)
  if (code === undefined || code === null) {
    return ""
  }
  return String(code).toUpperCase()
}

export function isExemptLine(line) {
  return taxTypeOf(line, "SLTS.ATT.TypeCode", "TC") === "FRE"
}

// เอกสารทั้งใบเป็นรายการยกเว้นภาษี — ไม่มีบรรทัดไหนที่คิด VAT เลย
export function isExemptDoc(doc) {
  if (!doc) {
    return false
  }
  if (taxTypeOf(doc, "SCTT.AHTS.ATT.TypeCode") === "FRE") {
    return true
  }
  const lines = Array.isArray(doc["SCTT.ISCTLI"]) ? doc["SCTT.ISCTLI"] : []
  return lines.length > 0 && lines.every(isExemptLine)
}

// คืนค่า null เมื่อเอกสารมีประเภทภาษีแบบเดียว หรือไม่มีข้อมูลรายการสินค้าให้แยก
// เมื่อแยกได้จะคืน { vat, nonVat, fre } โดย vat กับ fre เป็น null ได้ถ้าเอกสารไม่มีส่วนนั้น
export function getVatSplit(doc) {
  const docLines = Array.isArray(doc["SCTT.ISCTLI"]) ? doc["SCTT.ISCTLI"] : []
  let vatBasis = 0
  let freBasis = 0
  let hasZeroRateLine = false
  let hasVatLine = false

  for (const line of docLines) {
    // ต้องคัด FRE ออกก่อนดูอัตรา เพราะบรรทัดยกเว้นภาษีไม่มีอัตราติดมา
    // ถ้าปล่อยผ่านจะถูกนับเป็นบรรทัดที่คิด VAT แล้วยอดยกเว้นจะกลายเป็นยอดที่เสียภาษี
    if (isExemptLine(line)) {
      freBasis += getLineAmount(line)
      continue
    }
    if (getLineVatRate(line) === 0) {
      hasZeroRateLine = true
      continue
    }
    hasVatLine = true
    vatBasis += getLineAmount(line)
  }

  const hasVatPart = hasVatLine && vatBasis !== 0
  const hasFrePart = freBasis !== 0
  // ต้องมีอย่างน้อยสองกลุ่มถึงจะเรียกว่าแยกได้ มิฉะนั้นทั้งใบเป็นกลุ่มเดียว
  const partCount = (hasZeroRateLine ? 1 : 0) + (hasVatPart ? 1 : 0) + (hasFrePart ? 1 : 0)
  if (partCount < 2) {
    return null
  }

  const basis = Number(doc["SCTT.AHTS.STSHMS.TaxBasisTotalAmount"]) || 0
  const tax = Number(doc["SCTT.AHTS.STSHMS.TaxTotalAmount"]) || 0
  const total = Number(doc["SCTT.AHTS.STSHMS.GrandTotalAmount"]) || 0
  // ใบลดหนี้ (@DocType 4) ถูกกลับเครื่องหมายไว้แล้วตั้งแต่ใน store
  const direction = basis < 0 ? -1 : 1

  const vat = hasVatPart
    ? {
        basis: direction * vatBasis,
        tax: tax,
        total: direction * vatBasis + tax,
      }
    : null
  const fre = hasFrePart
    ? {
        basis: direction * freBasis,
        tax: 0,
        total: direction * freBasis,
      }
    : null

  // ส่วนอัตราศูนย์คิดจากยอดคงเหลือ เพื่อให้ผลรวมทุกส่วนเท่ากับยอดของเอกสารเสมอ
  const claimedBasis = (vat ? vat.basis : 0) + (fre ? fre.basis : 0)
  const claimedTotal = (vat ? vat.total : 0) + (fre ? fre.total : 0)
  const nonVat = {
    basis: basis - claimedBasis,
    tax: 0,
    total: total - claimedTotal,
  }
  return { vat, nonVat, fre }
}

export default getVatSplit
